import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, take } from 'rxjs/operators';
import { pickTruthy } from 'Util';
import { MonetizationInfo } from '../models/School';
import { User } from '../models/User';
import { MonetizationService } from './monetization.service';
import { UserService } from './user.service';

@Injectable({
	providedIn: 'root',
})
export class ProTrialService {
	constructor(private monetizationService: MonetizationService, private userService: UserService, private activeRoute: ActivatedRoute) {}

	disableAfterDate = new Date('2025-06-30');
	private reminderDate = new Date('2025-06-01');

	private combinedData$: Observable<{
		user: User;
		monetizationInfo: MonetizationInfo;
	}> = combineLatest([this.userService.effectiveUser$, this.monetizationService.monetizationInfo$]).pipe(
		map(([user, monetizationInfo]) => ({ user, monetizationInfo })),
		pickTruthy(),
		shareReplay(1)
	);

	private hasSeenUpsellIntro$ = this.userService.introsData$.pipe(
		map((introsData) => Boolean(introsData?.upsell_intro?.universal?.seen_version)),
		distinctUntilChanged()
	);
	private hasSeenUpsellReminder$ = this.userService.introsData$.pipe(
		map((introsData) => Boolean(introsData?.upsell_reminder?.universal?.seen_version)),
		distinctUntilChanged()
	);

	modalToShow$: Observable<'intro' | 'reminder'> = combineLatest([
		this.combinedData$,
		this.hasSeenUpsellReminder$,
		this.hasSeenUpsellIntro$,
		this.activeRoute.queryParams,
	]).pipe(
		map(([data, hasSeenReminder, hasSeenIntro, params]) => {
			if (!this.shouldShowProTrialInfo(data)) return '';

			const modal = params.modal;
			if (modal == 'pro_trial') {
				return 'intro';
			} else if (modal == 'pro_trial_reminder') {
				return 'reminder';
			}

			if (hasSeenReminder && hasSeenIntro) return '';

			const now = new Date();

			if (!hasSeenIntro && now < this.reminderDate) return 'intro';
			if (!hasSeenReminder && now >= this.reminderDate) return 'reminder';

			return '';
		}),
		filter((type): type is 'intro' | 'reminder' => !!type), // filter out '', null, undefined
		distinctUntilChanged(),
		shareReplay(1)
	);
	showProTooltipsAndTags$: Observable<boolean> = this.combinedData$.pipe(
		map((data) => this.shouldShowProTrialInfo(data)),
		distinctUntilChanged(),
		shareReplay(1)
	);

	private shouldShowProTrialInfo(data: { user: User; monetizationInfo: MonetizationInfo }): boolean {
		// Do not display after June 30th
		if (new Date() > this.disableAfterDate) return false;
		// Only display for admins
		const { monetizationInfo, user } = data;
		if (!user.isAdmin()) return false;

		const isCustomer = monetizationInfo.monetization_pipeline === 'customer';
		const proPlans = ['hall pass pro', 'smartpass one'];
		const hasPro = proPlans.some((plan) => monetizationInfo.recent_subscription_plan.toLowerCase().includes(plan));

		return isCustomer && !hasPro;
	}

	markIntroDialogAsSeen() {
		this.userService.introsData$
			.pipe(take(1))
			.subscribe((introsData) => this.userService.updateIntrosUpsellIntroDialogRequest(introsData, 'universal', '1'));
	}
	markReminderDialogAsSeen() {
		this.userService.introsData$
			.pipe(take(1))
			.subscribe((introsData) => this.userService.updateIntrosUpsellReminderDialogRequest(introsData, 'universal', '1'));
	}
}
